import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import NavBar from "./NavBar";
import Footer from "./Footer";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import "../styles/productDetails.css";
import SkeletonCard from "./SkeletonCard";
import { useBackground } from "../context/BackgroundContext";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

function ProductDetails() {
  const location = useLocation();
  const { collectionName, id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [additionalDescription, setAdditionalDescription] = useState("");
  const [quantity, setQuantity] = useState(1);
  const { theme } = useBackground();
  const db = getFirestore();
  const navigate = useNavigate();

  const stateProduct = location.state?.product;

  // Helper function to check if a variant is selected
  const isVariantSelected = (variantId) => selectedVariantId === variantId;

  // Helper function to get the selected color object
  const getSelectedColor = () => {
    if (selectedVariantId === "main") {
      return { id: "main", color: "original", image: product?.productImage };
    }
    return getColorVariantsWithUniqueIds().find(
      (color) => color.uniqueId === selectedVariantId
    );
  };

  // Helper to get color variants with guaranteed unique ids
  const getColorVariantsWithUniqueIds = () => {
    if (!product?.productColors) return [];
    const seen = new Set();
    return product.productColors.map((color, idx) => {
      let uniqueId = color.id || `variant-${idx}`;
      // If duplicate id, append index
      if (seen.has(uniqueId)) {
        uniqueId = `${uniqueId}-${idx}`;
      }
      seen.add(uniqueId);
      return { ...color, uniqueId };
    });
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (stateProduct) {
        setProduct(stateProduct);
        // Set initial main image to the product image
        setMainImage(stateProduct.productImage);
        setSelectedVariantId("main"); // Start with original selected
        setLoading(false);
      } else {
        try {
          const docRef = doc(db, collectionName, id);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const productData = docSnap.data();
            setProduct(productData);
            // Set initial main image to the product image
            setMainImage(productData.productImage);
            setSelectedVariantId("main"); // Start with original selected
          } else {
            console.error("No such product found in this collection!");
          }
        } catch (error) {
          console.error("Error fetching the product:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProduct();
  }, [db, collectionName, id, stateProduct]);

  const handleVariantClick = (variantId, imageUrl) => {
    console.log("Selecting variant:", variantId, "with image:", imageUrl);
    setSelectedVariantId(variantId);
    setMainImage(imageUrl);
  };

  const handleOriginalImageClick = () => {
    handleVariantClick("main", product.productImage);
  };

  if (loading) {
    return (
      <>
        <NavBar />
        <div
          className="container my-5"
          style={{ backgroundColor: theme.primary }}
        >
          <SkeletonCard type="productDetail" />
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return <div>No product data available</div>;
  }

  const handleGoBack = () => {
    if (stateProduct) {
      window.history.back();
    } else {
      window.location.href = "/home";
    }
  };

  const handleAddToCart = (product) => {
    const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
    const selectedColor = getSelectedColor();

    const itemIndex = cartItems.findIndex(
      (item) =>
        item.id === product.id && item.selectedColor?.id === selectedColor?.id
    );

    if (itemIndex !== -1) {
      cartItems[itemIndex].quantity += quantity;
    } else {
      const cartItem = {
        ...product,
        collection: collectionName,
        quantity,
        selectedColor,
      };

      if (additionalDescription.trim()) {
        cartItem.additionalDescription = additionalDescription;
      }

      cartItems.push(cartItem);
    }

    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    alert(`Added ${product.productTitle} x${quantity} to your cart!`);
    setAdditionalDescription("");
  };

  const handleProceedToPayment = () => {
    const selectedColor = getSelectedColor();
    const colorSlug = selectedColor?.color || "orignal";
    navigate(`/checkout/${collectionName}/${id}/${quantity}/${colorSlug}`);
  };

  return (
    <>
      <NavBar />
      <div className="goBackBtn ms-5 mt-2" role="button" onClick={handleGoBack}>
        <i
          className="bi bi-arrow-left-short fs-3"
          style={{ color: theme.text }}
        >
          Go Back
        </i>
      </div>
      <div
        className="product-details-wrapper"
        style={{
          backgroundColor: theme.background,
        }}
      >
        <div
          className="product-card"
          style={{
            backgroundColor: theme.accent,
            borderColor: theme.borderColor,
          }}
        >
          <div className="product-left">
            <div className="main-image-wrapper">
              <InnerImageZoom
                src={mainImage}
                zoomSrc={mainImage}
                alt={product.productTitle}
                zoomType="hover"
                zoomPreload={true}
                className="product-main-image"
              />
              <div className="thumbnail-row">
                <div
                  className={`thumbnail-item ${
                    isVariantSelected("main") ? "selected" : ""
                  }`}
                  onClick={() => handleOriginalImageClick()}
                  style={{ borderColor: theme.borderColor }}
                >
                  <img src={product.productImage} alt="Original" />
                </div>
                {getColorVariantsWithUniqueIds().map((color) => (
                  <div
                    key={color.uniqueId}
                    className={`thumbnail-item ${
                      isVariantSelected(color.uniqueId) ? "selected" : ""
                    }`}
                    onClick={() =>
                      handleVariantClick(color.uniqueId, color.image)
                    }
                    style={{ borderColor: theme.borderColor }}
                  >
                    <img src={color.image} alt={color.color} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="product-right">
            {!product.inStock && (
              <div className="soldOutContainer">Sold Out</div>
            )}
            <h1 className="product-title" style={{ color: theme.text }}>
              {product.productTitle}
            </h1>

            <h2 className="product-price" style={{ color: theme.text }}>
              PKR {product.productPrice}{" "}
              {product.oldPrice && (
                <span className="card-text text-start old_price">
                  {product.oldPrice} PKR
                </span>
              )}
            </h2>

            <p
              className={`product-description ${
                theme.themeName === "resin" ? "resin-text" : "fesishop-text"
              }`}
              dangerouslySetInnerHTML={{ __html: product.productDescription }}
            />

            <div className="quantity-selector d-flex align-items-center mb-3">
              <span className="me-2 fw-semibold" style={{ color: theme.text }}>
                Quantity:
              </span>
              <button
                className={`btn btn-outline-secondary border-0 ${
                  theme.themeName === "resin"
                    ? "resin-quantity-btn"
                    : "fesishop-quantity-btn"
                }`}
                style={{ color: theme.text }}
                onClick={() => setQuantity((prev) => Math.max(prev - 1, 1))}
              >
                –
              </button>
              <span className="mx-2" style={{ color: theme.text }}>
                {quantity}
              </span>
              <button
                className={`btn btn-outline-secondary border-0 ${
                  theme.themeName === "resin"
                    ? "resin-quantity-btn"
                    : "fesishop-quantity-btn"
                }`}
                style={{ color: theme.text }}
                onClick={() => setQuantity((prev) => prev + 1)}
              >
                +
              </button>
            </div>

            <button
              className="add-to-cart-button"
              disabled={!product.inStock}
              onClick={() => handleAddToCart(product)}
              style={{
                backgroundColor: theme.text,
                color: theme.primary,
              }}
            >
              Add to Cart
            </button>

            <button
              className="buy-now-button"
              onClick={handleProceedToPayment}
              disabled={!product.inStock}
              style={{
                backgroundColor: theme.primary,
                color: theme.text,
              }}
            >
              Buy It Now
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default ProductDetails;
